import {
  automationTemplateIdSchema,
  automationTemplates,
  getAutomationPlaybookIds,
  getAutomationTemplateById,
  type Automation,
  type AutomationTemplateId,
} from "@repo/app-types/entities/automation";
import {
  asPlaybookId,
  checkPlaybookSelected,
  type PlaybookId,
  type PlaybookSelection,
} from "@repo/app-types/entities/playbook";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FilterBar } from "@repo/ui/Filters/filterBar";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import {
  SimpleSelect,
  type SimpleSelectOption,
  type SimpleSelectSection,
} from "@repo/ui/Select/simpleSelect";
import { SegmentedControl } from "@repo/ui/toggleButton/segmentedControl";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useMemo, useState, type FC } from "react";
import { DialogTrigger } from "react-aria-components";
import { useNavigate, useParams } from "react-router";
import {
  useAllAutomationsQuery,
  useUpdateAutomationMutation,
} from "../../api/entities/automation.js";
import {
  PageBody,
  PageLayout,
  PageLayoutHeight,
  TableCard,
} from "../../components/layout/pageLayout.js";
import { buildNewAutomationLocation } from "../../features/automations/automationEditorUrlState.js";
import { AutomationsTable } from "../../features/automations/automationsTable.js";
import { AutomationTemplateChooserDialog } from "../../features/automations/automationTemplateChooserDialog.js";
import { AiCreditsExhaustedIndicator } from "../../features/billing/aiCreditsExhaustedIndicator.js";
import { FilterResultSummary } from "../../features/filters/filterResultSummary.js";
import {
  Can,
  useTeammateAuthorization,
} from "../../helpers/teammateAuthorizationContext.js";
import {
  buildDynamicSelectionOptions,
  buildPlaybookAndVoiceAgentSections,
  checkIsVoiceAgentOption,
  DYNAMIC_SELECTION_OPTIONS,
  isDynamicSelectionOptionId,
  usePlaybookAndVoiceAgentOptions,
} from "../../hooks/usePlaybookAndVoiceAgentOptions.js";

const statusFilters = [
  { id: "active", label: "Active" },
  { id: "all", label: "All" },
] as const;

type StatusFilter = (typeof statusFilters)[number]["id"];

const ALL_PLAYBOOKS = "all";
const ALL_TEMPLATES = "all-templates";

export const AutomationsPage: FC = () => {
  const navigate = useNavigate();
  const { playbookId: playbookIdParam } = useParams();

  const [searchQuery, setSearchQuery] = useState("");
  const [playbookFilter, setPlaybookFilter] = useState<string>(ALL_PLAYBOOKS);
  const [templateFilter, setTemplateFilter] = useState<string>(ALL_TEMPLATES);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const teammateAuthorization = useTeammateAuthorization();
  // Reviewers can view automations but not modify them, so the enable switches
  // render disabled.
  const isReadOnly = !teammateAuthorization.can(
    PermissionActions.UPDATE,
    PermissionSubjects.AUTOMATIONS,
  );

  const { data: automations, isLoading } = useAllAutomationsQuery();
  const { options: playbookAndVoiceAgentOptions } =
    usePlaybookAndVoiceAgentOptions();
  const [updateAutomation] = useUpdateAutomationMutation();

  // Includes voice agents (keyed by their backing playbook), so scopes that
  // analyze voice agent calls render the agent's name.
  const playbookNamesById = useMemo(
    () =>
      new Map<PlaybookId, string>(
        playbookAndVoiceAgentOptions.map((option) => [
          option.value,
          option.label,
        ]),
      ),
    [playbookAndVoiceAgentOptions],
  );

  const voiceAgentPlaybookIds = useMemo(
    () =>
      new Set<PlaybookId>(
        playbookAndVoiceAgentOptions
          .filter(checkIsVoiceAgentOption)
          .map((option) => option.value),
      ),
    [playbookAndVoiceAgentOptions],
  );

  const playbookFilterSections = useMemo<
    readonly SimpleSelectSection<string>[]
  >(
    () => [
      {
        id: "dynamic",
        options: buildDynamicSelectionOptions(
          voiceAgentPlaybookIds.size > 0,
        ).map((option) => ({ value: option.id, label: option.label })),
      },
      ...buildPlaybookAndVoiceAgentSections(playbookAndVoiceAgentOptions),
    ],
    [playbookAndVoiceAgentOptions, voiceAgentPlaybookIds],
  );

  /** The filter as a scope: "all" = everything, an id pins one playbook. */
  const playbookFilterSelection = useMemo<PlaybookSelection>(
    () =>
      isDynamicSelectionOptionId(playbookFilter)
        ? DYNAMIC_SELECTION_OPTIONS[playbookFilter].selection
        : { type: "playbooks", ids: [asPlaybookId(playbookFilter)] },
    [playbookFilter],
  );

  const templateFilterOptions = useMemo<readonly SimpleSelectOption<string>[]>(
    () => [
      { value: ALL_TEMPLATES, label: "All automations" },
      ...automationTemplateIdSchema.options.map((templateId) => ({
        value: templateId,
        label: getAutomationTemplateById(templateId).name,
      })),
    ],
    [],
  );

  const filteredAutomations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return (automations ?? []).filter((automation) => {
      if (statusFilter === "active" && !automation.is_enabled) {
        return false;
      }
      // The "all" filter skips the check entirely so automations without any
      // playbook ids still show.
      if (
        playbookFilterSelection.type !== "all" &&
        !getAutomationPlaybookIds(automation.settings).some((playbookId) =>
          checkPlaybookSelected(
            playbookFilterSelection,
            playbookId,
            voiceAgentPlaybookIds.has(playbookId),
          ),
        )
      ) {
        return false;
      }
      if (
        templateFilter !== ALL_TEMPLATES &&
        automation.template_id !== templateFilter
      ) {
        return false;
      }
      if (query === "") {
        return true;
      }
      const template = getAutomationTemplateById(automation.template_id);
      return (
        automation.name.toLowerCase().includes(query) ||
        template.name.toLowerCase().includes(query)
      );
    });
  }, [
    automations,
    statusFilter,
    playbookFilterSelection,
    voiceAgentPlaybookIds,
    templateFilter,
    searchQuery,
  ]);

  const hasFilterChanges =
    searchQuery.trim() !== "" ||
    playbookFilter !== ALL_PLAYBOOKS ||
    templateFilter !== ALL_TEMPLATES ||
    statusFilter !== "all";

  const onResetFilters = () => {
    setSearchQuery("");
    setPlaybookFilter(ALL_PLAYBOOKS);
    setTemplateFilter(ALL_TEMPLATES);
    setStatusFilter("all");
  };

  // Nothing is created here: the new-automation editor starts from the
  // template's defaults, and Save creates the automation.
  const onChooseTemplate = (templateId: AutomationTemplateId) => {
    if (!playbookIdParam) {
      throw new Error("Missing playbookId URL param");
    }
    navigate(
      buildNewAutomationLocation({
        playbookId: asPlaybookId(playbookIdParam),
        templateId,
      }),
    );
  };

  const onToggleEnabled = async (
    automation: Automation,
    isEnabled: boolean,
  ) => {
    await updateAutomation({
      id: automation.id,
      is_enabled: isEnabled,
    });
  };

  return (
    <PageLayout height={PageLayoutHeight.Bounded}>
      <PageHeader
        title="Automations"
        actions={
          <>
            <AiCreditsExhaustedIndicator surface="automations" />
            <Can
              I={PermissionActions.CREATE}
              a={PermissionSubjects.AUTOMATIONS}
              passThrough={true}
            >
              {(allowed) => (
                <DialogTrigger>
                  <Button
                    variant={ButtonVariant.Primary}
                    size={Size.SMALL}
                    icon={<IconPlus className="size-4 shrink-0" />}
                    isDisabled={!allowed}
                  >
                    Create automation
                  </Button>
                  <AutomationTemplateChooserDialog
                    templates={automationTemplates}
                    onChoose={onChooseTemplate}
                  />
                </DialogTrigger>
              )}
            </Can>
          </>
        }
        filterBar={
          <FilterBar
            isLoading={isLoading}
            leadingItems={
              <div className="w-56 shrink-0">
                <Input
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  placeholder="Search automations…"
                  leftIcon={<IconSearch className="size-4 text-gray-400" />}
                  maxLength={100}
                  size={Size.SMALL}
                />
              </div>
            }
            items={
              <>
                <SimpleSelect
                  label="Playbook filter"
                  isLabelHidden
                  sections={playbookFilterSections}
                  value={playbookFilter}
                  onChange={setPlaybookFilter}
                  size={Size.SMALL}
                />
                <SimpleSelect
                  label="Automation template filter"
                  isLabelHidden
                  options={templateFilterOptions}
                  value={templateFilter}
                  onChange={setTemplateFilter}
                  size={Size.SMALL}
                />
              </>
            }
            resultSummary={
              <FilterResultSummary
                count={filteredAutomations.length}
                denominatorCount={
                  hasFilterChanges ? (automations?.length ?? 0) : undefined
                }
              />
            }
            onReset={hasFilterChanges ? onResetFilters : undefined}
            rightActions={
              <SegmentedControl
                aria-label="Status"
                size={Size.SMALL}
                options={statusFilters}
                value={statusFilter}
                onChange={setStatusFilter}
              />
            }
          />
        }
      />

      <PageBody fills>
        <TableCard>
          <AutomationsTable
            automations={filteredAutomations}
            isLoading={isLoading}
            isReadOnly={isReadOnly}
            playbookNamesById={playbookNamesById}
            emptyMessage={
              (automations?.length ?? 0) > 0
                ? "No automations match your filters."
                : "No automations yet. Create your first automation to get started."
            }
            onAutomationAction={(automation) => {
              navigate(automation.id);
            }}
            onToggleEnabled={(automation, isEnabled) => {
              void onToggleEnabled(automation, isEnabled);
            }}
          />
        </TableCard>
      </PageBody>
    </PageLayout>
  );
};
